import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useDarkMode } from "../../../context/ThemeContext";
import { useDebounce } from "../../../hooks/useDebounce";
import PaginationControls from "../../../components/base/PaginationControls";
import {
  EMPTY_FILTERS,
  applyQuick,
  buildAccounts,
  filterUnits,
  isQuick,
  type Billing,
  type CheckFilter,
  type CuadreAccount,
  type PlatformFilter,
  type QuickView,
  type ReportFilter,
  type UnitFilters,
} from "../../../features/sg/cuadre";
import { useCuadreUnits } from "../../../features/sg/cuadreSource";
import { timeAgo } from "../../../features/sg/format";
import { downloadXlsx } from "../../../features/sg/xlsx";
import { fileDate } from "../../../features/sg/exportRows";
import { accountsSheet, unitsSheet } from "../../../features/sg/informes";
import { notifyError, notifyOk } from "../../../features/sg/notify";
import { PLATFORM_META } from "../../../features/sg/platforms";
import CuadreFilters from "./CuadreFilters";
import UnitsTable from "./UnitsTable";
import AccountsTable from "./AccountsTable";
import OriginFixModal from "./OriginFixModal";
import { originChanges, type OriginChange } from "../../../features/sg/origen";
import { fmt } from "./ui";

const PER_PAGE = 50;

type View = "unidades" | "cuentas";
type AccountFilter = "" | "mismatch" | "silent" | "none";
type AccountSort = "units" | "silent" | "name";

// El estado vive en la URL: una vista filtrada se comparte con un link y el
// botón atrás regresa al filtro anterior.
function readFilters(sp: URLSearchParams): UnitFilters {
  const acc = Number(sp.get("cuenta"));
  return {
    q: sp.get("q") ?? "",
    billing: (sp.get("fact") as Billing) || "",
    report: (sp.get("rep") as ReportFilter) || "",
    platform: (sp.get("plat") as PlatformFilter) || "",
    condition: sp.get("etq") ?? "",
    account: Number.isFinite(acc) && acc > 0 ? acc : null,
    mismatch: sp.get("desc") === "1",
    check: (sp.get("rev") as CheckFilter) || "",
  };
}

const PARAM: Record<keyof UnitFilters, string> = {
  q: "q",
  billing: "fact",
  report: "rep",
  platform: "plat",
  condition: "etq",
  account: "cuenta",
  mismatch: "desc",
  check: "rev",
};

const UNIT_QUICK: { key: QuickView; tone: string }[] = [
  { key: "billableSilent", tone: "#EF4444" },
  { key: "notBillableReporting", tone: "#F59E0B" },
  { key: "noPlatform", tone: "#9CA3AF" },
  { key: "multi", tone: "#F59E0B" },
];

export default function Cuadre() {
  const { t } = useTranslation("sg");
  const { isDarkMode } = useDarkMode();
  const queryClient = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fix, setFix] = useState<OriginChange[] | null>(null);

  const view: View = sp.get("v") === "cuentas" ? "cuentas" : "unidades";
  const filters = readFilters(sp);
  const [search, setSearch] = useState(filters.q);
  const q = useDebounce(search, 250);
  const effective = useMemo(() => ({ ...filters, q }), [sp, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const page = Math.max(1, Number(sp.get("p")) || 1);
  const accFilter = (sp.get("af") as AccountFilter) || "";
  const accSort = (sp.get("orden") as AccountSort) || "units";

  const { source, units, progress, resetProgress } = useCuadreUnits();
  const accounts = useMemo(() => (source.data ? buildAccounts(units, source.data.accounts) : []), [units, source.data]);

  const update = (patch: Record<string, string | null>, replace = false) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    }
    if (!("p" in patch)) next.delete("p");
    setSp(next, { replace });
  };

  const setFilters = (patch: Partial<UnitFilters>) =>
    update(
      Object.fromEntries(
        Object.entries(patch).map(([k, v]) => [PARAM[k as keyof UnitFilters], v === true ? "1" : v === false || v == null ? null : String(v)])
      )
    );

  const replaceFilters = (f: UnitFilters, extra: Record<string, string | null> = {}) => {
    const patch: Record<string, string | null> = { ...extra };
    for (const k of Object.keys(PARAM) as (keyof UnitFilters)[]) {
      if (k === "q") continue;
      const v = f[k];
      patch[PARAM[k]] = v === true ? "1" : v === false || v == null || v === "" ? null : String(v);
    }
    update(patch);
  };

  // ── Unidades ──
  const filteredUnits = useMemo(() => filterUnits(units, effective), [units, effective]);
  const quickCounts = useMemo(() => {
    const base = { ...EMPTY_FILTERS, q };
    return Object.fromEntries(UNIT_QUICK.map(({ key }) => [key, filterUnits(units, { ...base, ...applyQuick(key), q }).length]));
  }, [units, q]);

  // ── Cuentas ──
  const accountsById = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const mismatchCount = useMemo(() => accounts.filter((a) => a.mismatch).length, [accounts]);
  const filteredAccounts = useMemo(() => {
    const nq = q.trim().normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    const keep = (a: CuadreAccount) =>
      a.units > 0 || a.mismatch
        ? (!nq || a.name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().includes(nq)) &&
          (accFilter === "mismatch" ? a.mismatch : accFilter === "silent" ? a.silent > 0 : accFilter === "none" ? a.byPlatform.none > 0 : true)
        : false;
    const rows = accounts.filter(keep);
    const by: Record<AccountSort, (a: CuadreAccount, b: CuadreAccount) => number> = {
      units: (a, b) => b.units - a.units,
      silent: (a, b) => b.silent - a.silent,
      name: () => 0,
    };
    return rows.sort((a, b) => by[accSort](a, b) || a.name.localeCompare(b.name, "es"));
  }, [accounts, q, accFilter, accSort]);

  const fixable = useMemo(() => originChanges(filteredAccounts), [filteredAccounts]);

  const list = view === "unidades" ? filteredUnits : filteredAccounts;
  const totalPages = Math.max(1, Math.ceil(list.length / PER_PAGE));
  const pageRows = list.slice((Math.min(page, totalPages) - 1) * PER_PAGE, Math.min(page, totalPages) * PER_PAGE);

  const activeAccount = filters.account != null ? accountsById.get(filters.account) : undefined;
  const activeChips: { label: ReactNode; clear: () => void }[] = [];
  if (view === "unidades") {
    if (filters.account != null) activeChips.push({ label: t("cuadre.chips.account", { name: activeAccount?.name ?? `#${filters.account}` }), clear: () => setFilters({ account: null }) });
    if (filters.mismatch) activeChips.push({ label: t("cuadre.quick.mismatch"), clear: () => setFilters({ mismatch: false }) });
    if (filters.billing) activeChips.push({ label: t(`cuadre.billing.${filters.billing}`), clear: () => setFilters({ billing: "" }) });
    if (filters.report) activeChips.push({ label: t(`cuadre.report.${filters.report}`), clear: () => setFilters({ report: "" }) });
    if (filters.platform)
      activeChips.push({
        label: filters.platform in PLATFORM_META ? PLATFORM_META[filters.platform as keyof typeof PLATFORM_META].label : t(`cuadre.platform.${filters.platform}`),
        clear: () => setFilters({ platform: "" }),
      });
    if (filters.check) activeChips.push({ label: t(`cuadre.check.${filters.check}`), clear: () => setFilters({ check: "" }) });
    if (filters.condition) activeChips.push({ label: filters.condition === "-" ? t("cuadre.filters.noCondition") : filters.condition, clear: () => setFilters({ condition: "" }) });
  }
  const activeCount = activeChips.length;

  const refresh = () => {
    resetProgress();
    queryClient.invalidateQueries({ queryKey: ["sg-cuadre-source"] });
  };

  async function exportView() {
    setExporting(true);
    try {
      const sheet = view === "unidades" ? unitsSheet(filteredUnits) : accountsSheet(filteredAccounts);
      await downloadXlsx(`cuadre_${view === "unidades" ? "unidades" : "cuentas"}_${fileDate()}.xlsx`, sheet.name, sheet.headers, sheet.rows);
      notifyOk(t("cuadre.exported", { n: fmt(list.length) }));
    } catch (e: any) {
      notifyError(e.message);
    } finally {
      setExporting(false);
    }
  }

  const card = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const strong = isDarkMode ? "text-white" : "text-gray-900";

  return (
    <div className="h-full min-h-0 w-full overflow-y-auto">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Encabezado: el único lugar con los totales */}
        <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold tracking-tight ${strong}`}>{t("cuadre.title")}</h1>
            <p className={`text-sm mt-1 ${muted}`}>
              {source.data
                ? t("cuadre.totals", { units: fmt(units.length), accounts: fmt(source.data.accounts.length), ago: timeAgo(source.data.generatedAt) })
                : t("cuadre.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div role="tablist" aria-label={t("cuadre.view")} className={`inline-flex p-1 rounded-xl ${isDarkMode ? "bg-dark-card" : "bg-gray-100"}`}>
              {(["unidades", "cuentas"] as View[]).map((v) => (
                <button
                  key={v}
                  role="tab"
                  aria-selected={view === v}
                  onClick={() => update({ v: v === "unidades" ? null : v })}
                  className={`px-3.5 py-1.5 text-sm rounded-lg font-medium transition-[background-color,color,transform] duration-150 active:scale-[0.97] ${
                    view === v ? (isDarkMode ? "bg-dark-hover text-white" : "bg-white text-gray-900 shadow-sm") : muted
                  }`}
                >
                  {t(`cuadre.views.${v}`)}
                </button>
              ))}
            </div>
            <button
              onClick={exportView}
              disabled={!source.data || exporting || !list.length}
              className={`h-9 px-3 rounded-xl border text-sm font-medium flex items-center gap-2 transition-transform active:scale-[0.97] disabled:opacity-50 ${
                isDarkMode ? "border-dark-border text-gray-200 hover:bg-dark-card" : "border-gray-200 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <i className="ri-download-2-line" aria-hidden="true" />
              {exporting ? t("common.exporting") : t("cuadre.export")}
            </button>
            <button
              onClick={refresh}
              disabled={source.isFetching}
              title={t("cuadre.refresh")}
              aria-label={t("cuadre.refresh")}
              className={`w-9 h-9 rounded-xl border flex items-center justify-center transition-transform active:scale-95 disabled:opacity-50 ${
                isDarkMode ? "border-dark-border text-gray-300 hover:bg-dark-card" : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <i className={`ri-refresh-line ${source.isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </header>

        {source.isError ? (
          <div className={`rounded-2xl border p-6 text-sm text-red-500 ${card}`}>{(source.error as Error)?.message || t("cuadre.loadError")}</div>
        ) : !source.data ? (
          <div className={`rounded-2xl border p-12 flex flex-col items-center gap-3 text-sm ${card} ${muted}`}>
            <i className="ri-loader-4-line animate-spin text-2xl" />
            {t(`cuadre.loading.${progress.stage}`, { n: fmt(progress.n) })}
          </div>
        ) : (
          <>
            {/* Vistas rápidas: las preguntas del cuadre, cada una es un filtro */}
            <section aria-label={t("cuadre.quickTitle")} className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              {UNIT_QUICK.map(({ key, tone }) => {
                const active = view === "unidades" && isQuick(filters, key);
                return (
                  <QuickCard
                    key={key}
                    active={active}
                    tone={tone}
                    count={quickCounts[key]}
                    label={t(`cuadre.quick.${key}`)}
                    hint={t(`cuadre.quickHint.${key}`)}
                    isDarkMode={isDarkMode}
                    onClick={() => (active ? replaceFilters(EMPTY_FILTERS) : replaceFilters(applyQuick(key), { v: null }))}
                  />
                );
              })}
              <QuickCard
                active={view === "cuentas" && accFilter === "mismatch"}
                tone="#F59E0B"
                count={mismatchCount}
                label={t("cuadre.quick.mismatch")}
                hint={t("cuadre.quickHint.mismatch")}
                isDarkMode={isDarkMode}
                onClick={() => update(view === "cuentas" && accFilter === "mismatch" ? { af: null } : { v: "cuentas", af: "mismatch" })}
              />
            </section>

            <div className={`rounded-2xl border ${card}`}>
              {/* Barra: buscador + filtros activos */}
              <div className={`p-4 border-b flex flex-col gap-3 ${isDarkMode ? "border-dark-border" : "border-gray-200"}`}>
                <div className="flex flex-col sm:flex-row gap-2">
                  <label className="relative flex-1">
                    <span className="sr-only">{t("cuadre.search")}</span>
                    <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm ${muted}`} aria-hidden="true" />
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        update({ q: e.target.value || null }, true);
                      }}
                      placeholder={t(view === "unidades" ? "cuadre.searchUnits" : "cuadre.searchAccounts")}
                      className={`w-full h-10 pl-9 pr-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        isDarkMode ? "bg-dark-card border-dark-border text-white placeholder-gray-500" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                      }`}
                    />
                  </label>
                  {view === "unidades" ? (
                    <button
                      type="button"
                      onClick={() => setShowFilters((s) => !s)}
                      aria-expanded={showFilters}
                      className={`xl:hidden h-10 px-3 rounded-xl border text-sm font-medium flex items-center justify-center gap-2 ${
                        isDarkMode ? "border-dark-border text-gray-200" : "border-gray-200 text-gray-700"
                      }`}
                    >
                      <i className="ri-filter-3-line" aria-hidden="true" />
                      {t("cuadre.filtersBtn")}
                      {activeCount > 0 && <span className="rounded-full bg-primary-500 text-black text-[11px] px-1.5 font-semibold">{activeCount}</span>}
                    </button>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {fixable.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setFix(fixable)}
                          className="h-10 px-3 rounded-xl bg-primary-500 hover:bg-primary-400 text-black text-sm font-semibold flex items-center gap-2 transition-[background-color,transform] duration-150 active:scale-[0.97]"
                        >
                          <i className="ri-git-merge-line" aria-hidden="true" />
                          {t("origen.button", { n: fmt(fixable.length) })}
                        </button>
                      )}
                      <select
                        aria-label={t("cuadre.accFilter")}
                        value={accFilter}
                        onChange={(e) => update({ af: e.target.value || null })}
                        className={`h-10 px-3 rounded-xl border text-sm ${isDarkMode ? "bg-dark-card border-dark-border text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                      >
                        {(["", "mismatch", "silent", "none"] as AccountFilter[]).map((f) => (
                          <option key={f} value={f}>
                            {t(`cuadre.accFilters.${f || "all"}`)}
                          </option>
                        ))}
                      </select>
                      <select
                        aria-label={t("cuadre.sort")}
                        value={accSort}
                        onChange={(e) => update({ orden: e.target.value === "units" ? null : e.target.value })}
                        className={`h-10 px-3 rounded-xl border text-sm ${isDarkMode ? "bg-dark-card border-dark-border text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                      >
                        {(["units", "silent", "name"] as AccountSort[]).map((s) => (
                          <option key={s} value={s}>
                            {t(`cuadre.sorts.${s}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className={`tabular-nums font-semibold ${strong}`}>{t(view === "unidades" ? "cuadre.countUnits" : "cuadre.countAccounts", { n: fmt(list.length) })}</span>
                  {activeChips.map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={c.clear}
                      className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1.5 py-0.5 text-xs transition-colors ${
                        isDarkMode ? "bg-white/10 text-gray-200 hover:bg-white/15" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {c.label}
                      <i className="ri-close-line" aria-label={t("cuadre.chips.remove")} />
                    </button>
                  ))}
                  {activeAccount && (
                    <Link to={`/sg/users/edit/${activeAccount.id}`} className="text-xs text-primary-500 hover:underline">
                      {t("cuadre.actions.editAccount")}
                    </Link>
                  )}
                  {activeCount > 1 && (
                    <button type="button" onClick={() => replaceFilters(EMPTY_FILTERS)} className={`text-xs hover:underline ${muted}`}>
                      {t("cuadre.chips.clear")}
                    </button>
                  )}
                </div>
              </div>

              <div className={view === "unidades" ? "xl:grid xl:grid-cols-[232px_1fr]" : ""}>
                {view === "unidades" && (
                  <aside
                    className={`${showFilters ? "block" : "hidden"} xl:block p-4 border-b xl:border-b-0 xl:border-r ${
                      isDarkMode ? "border-dark-border" : "border-gray-200"
                    }`}
                  >
                    <CuadreFilters units={units} filters={effective} onChange={setFilters} isDarkMode={isDarkMode} />
                  </aside>
                )}
                <div className="min-w-0 px-4 pb-4">
                  {pageRows.length ? (
                    view === "unidades" ? (
                      <UnitsTable rows={pageRows as typeof filteredUnits} onAccount={(id) => setFilters({ account: id })} isDarkMode={isDarkMode} />
                    ) : (
                      <AccountsTable
                        rows={pageRows as CuadreAccount[]}
                        onUnits={(id) => replaceFilters({ ...EMPTY_FILTERS, account: id }, { v: null, af: null })}
                        onFix={(a) => setFix(originChanges([a]))}
                        isDarkMode={isDarkMode}
                      />
                    )
                  ) : (
                    <div className={`py-14 text-center text-sm ${muted}`}>
                      <i className="ri-filter-off-line text-2xl block mb-2" aria-hidden="true" />
                      {t("cuadre.empty")}
                      {(activeCount > 0 || q || accFilter) && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearch("");
                            replaceFilters(EMPTY_FILTERS, { q: null, af: null });
                          }}
                          className="block mx-auto mt-2 text-primary-500 hover:underline"
                        >
                          {t("cuadre.chips.clear")}
                        </button>
                      )}
                    </div>
                  )}
                  {totalPages > 1 && (
                    <PaginationControls currentPage={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={(p) => update({ p: p > 1 ? String(p) : null })} />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {fix && (
        <OriginFixModal
          changes={fix}
          isDarkMode={isDarkMode}
          onClose={() => setFix(null)}
          onApplied={() => queryClient.invalidateQueries({ queryKey: ["sg-cuadre-source"] })}
        />
      )}
    </div>
  );
}

function QuickCard({
  active,
  tone,
  count,
  label,
  hint,
  isDarkMode,
  onClick,
}: {
  active: boolean;
  tone: string;
  count: number;
  label: string;
  hint: string;
  isDarkMode: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`group relative flex flex-col justify-start text-left rounded-2xl border p-4 overflow-hidden transition-[border-color,background-color,transform] duration-150 active:scale-[0.98] ${
        active
          ? isDarkMode
            ? "bg-dark-card border-primary-500/60"
            : "bg-white border-primary-500 shadow-sm"
          : isDarkMode
            ? "bg-dark-surface border-dark-border hover:border-dark-hover"
            : "bg-white border-gray-200 hover:border-gray-300"
      }`}
    >
      <span className="absolute left-0 top-4 bottom-4 w-[3px] rounded-r-full" style={{ background: tone }} aria-hidden="true" />
      <span className={`block text-3xl font-bold tabular-nums tracking-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}>{fmt(count)}</span>
      <span className={`mt-1 block text-sm font-medium leading-snug ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>{label}</span>
      <span className={`mt-0.5 block text-xs leading-snug ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>{hint}</span>
      {active && <i className="ri-close-circle-fill absolute top-3 right-3 text-primary-500" aria-hidden="true" />}
    </button>
  );
}
